/** @typedef {{ title: string; body: string }} ImpactCard */

/**
 * @typedef {{
 *   heroTitle: string;
 *   heroSubtitle: string;
 *   mechanismHeading: string;
 *   mechanismBody: string;
 *   riveSrc: string;
 *   impactCards: ImpactCard[];
 *   standardHeading: string;
 *   standardParagraphs: string[];
 *   standardQuote?: string;
 * }} ServiceContent
 */

/** @type {string[]} */
export const SERVICE_SLUGS = [
  "fingerprinting-hash-matching",
  "hidden-leak-detection",
  "visual-impersonation-ai",
  "multi-layer-distribution",
  "creator-protection-circle",
  "secure-creator-infrastructure",
];

/** @type {Record<string, ServiceContent>} */
export const SERVICES_BY_SLUG = {
  "fingerprinting-hash-matching": {
    heroTitle: "Fingerprinting & Hash Matching",
    heroSubtitle:
      "Stop the leaks. Turn digital piracy back into subscriber revenue.",
    mechanismHeading: "How It Works",
    mechanismBody:
      "Standard takedown services rely on text-based keywords or file names - a method easily bypassed by pirates who simply rename or slightly alter the file.\n\nThe SERVED infrastructure works differently. We utilize advanced digital fingerprinting and hash-matching technology. Before your content ever becomes a target, our system registers its unique digital signature. When an unauthorized copy appears online, our scanners recognize it immediately, regardless of what it has been renamed or where it has been reposted. Instead of chasing filenames, we track the content itself and initiate legally binding removal actions to restore control over where your work lives online.",
    riveSrc: "/rive/revenue.riv",
    impactCards: [
      {
        title: "Siphoned Revenue",
        body: "Pirated uploads create a free alternative to your paywall, quietly pulling attention and subscription revenue away from the platforms where your work is meant to live.",
      },
      {
        title: "Precision Removal",
        body: "Our fingerprinting and hash-matching technology allows us to identify the exact files being redistributed, even when pirates attempt to disguise them through renaming or reposting.",
      },
      {
        title: "Subscriber Recovery",
        body: "As unauthorized copies are removed and dead links replace stolen uploads, viewers searching for your content are naturally redirected back to the official source - behind your monetized paywall.",
      },
    ],
    standardHeading: "The SERVED Approach: Enforced Rights",
    standardParagraphs: [
      "We don't just protect your content; we actively recover your lost conversions.",
      "We combine proprietary hash-matching technology with our established, aggressive legal framework to ensure that once a file is removed, it stays removed. Our goal is not just compliance, but the systematic restoration of your revenue stream and digital authority.",
    ],
    standardQuote:
      '"Piracy is not a visibility strategy. We built SERVED so creators can protect value and keep full control of where their work lives." - Founders',
  },

  "hidden-leak-detection": {
    heroTitle: "Hidden Leak Detection",
    heroSubtitle:
      "Find stolen content where it first appears. Detect leaks early across private forums, archives, and hidden networks.",
    mechanismHeading: "How It Works",
    mechanismBody:
      "Leaks rarely stay visible on public search results for long. They move into private channels, invite-only forums, and archived file networks that traditional monitoring tools rarely reach.\n\nSERVED monitoring extends into those deeper layers of the internet. Our system quietly tracks signals across leak forums, file mirrors, reseller listings, and closed communities where stolen creator content is commonly circulated. When protected content appears in those environments, our team receives early signals that allow enforcement to begin before links spread across larger piracy networks.",
    riveSrc: "/rive/scanner.riv",
    impactCards: [
      {
        title: "Early Detection",
        body: "Identify redistribution and resale of your content within private forums, archive sites, and nonindexed communities before those leaks reach larger piracy hubs.",
      },
      {
        title: "Signal Clarity",
        body: "Every detection is reviewed with source context so you understand where the content appeared, how widely it has spread, and what enforcement steps follow.",
      },
      {
        title: "Faster Containment",
        body: "Earlier detection shortens the time between discovery and removal, reducing the window where stolen content can circulate or be replicated elsewhere.",
      },
    ],
    standardHeading: "The SERVED Approach: Enforced Rights",
    standardParagraphs: [
      "We don't just protect your content; we actively recover your lost conversions.",
      "Hidden Leak Detection pairs automated discovery with our established legal pipeline. Findings feed the same aggressive enforcement framework used across SERVED - so removals stick and repeat offenders are tracked, not forgotten.",
    ],
    standardQuote:
      '"Threats do not disappear when they leave the open web. Our job is to find them first and shut down momentum before it scales." - Founders',
  },

  "visual-impersonation-ai": {
    heroTitle: "Visual Impersonation AI",
    heroSubtitle:
      "Catch lookalike media, deepfakes, and recycled clips that keyword tools often miss.",
    mechanismHeading: "How It Works",
    mechanismBody:
      "Pirates frequently rename files, remove metadata, or crop content in an attempt to bypass traditional detection tools. Visual Impersonation AI works differently. Instead of relying on filenames or text signals, our system compares visual patterns, motion, and structural similarity against your protected originals. Even when clips are re-encoded, partially edited, or combined into compilations, the underlying imagery remains recognizable - allowing our system to flag the content and initiate enforcement under your protection program.",
    riveSrc: "/rive/notifications.riv",
    impactCards: [
      {
        title: "AI Deepfake Detection",
        body: "Identify synthetic media and manipulated clips that imitate your likeness or repurpose your original content.",
      },
      {
        title: "Impersonation Mapping",
        body: "Related uploads and channels that reuse the same stolen segments can be linked together, helping identify broader repost networks rather than isolated links.",
      },
      {
        title: "Brand Protection",
        body: "Reduce misleading reposts, impersonation edits, and unauthorized clones that trade on your likeness and erode trust with subscribers and platforms.",
      },
    ],
    standardHeading: "The SERVED Approach: Enforced Rights",
    standardParagraphs: [
      "We don't just protect your content; we actively recover your lost conversions.",
      "Visual signals feed the same enforcement stack as fingerprinting and hash matching: verified matches become takedowns, retractions, and repeat-offender records - not alerts you have to chase alone.",
    ],
    standardQuote:
      '"If your likeness can be copied, your protection has to recognize more than filenames. We designed SERVED for that reality." - Founders',
  },

  "multi-layer-distribution": {
    heroTitle: "Multi-Layer Distribution",
    heroSubtitle:
      "Track how stolen content spreads across mirrors, reposts, and copy networks.",
    mechanismHeading: "How It Works",
    mechanismBody:
      "When content leaks, it rarely stays in one place. A single upload is quickly copied, mirrored, and redistributed across multiple sites and file hosts. Traditional takedown services remove individual links, but new copies often appear just as quickly.\n\nSERVED maps how leaked files move between platforms and mirrors, allowing our system to identify the sources that feed the wider repost network. By targeting the origins and highest traffic redistribution points first, removals become far more effective and prevent the same content from repeatedly resurfacing across new links.",
    riveSrc: "/rive/distribution.riv",
    impactCards: [
      {
        title: "Source-Level Removal",
        body: "Instead of chasing endless duplicate links, enforcement focuses on the original sources and major mirrors responsible for spreading the content.",
      },
      {
        title: "Distribution Mapping",
        body: "Track how stolen files move across platforms, hosts, and repost channels so removals target the locations driving the most exposure.",
      },
      {
        title: "Continuous Containment",
        body: "Ongoing monitoring detects re-uploads and new repost locations, helping ensure leaks stay contained even as pirates attempt to redistribute the files again.",
      },
    ],
    standardHeading: "The SERVED Approach: Enforced Rights",
    standardParagraphs: [
      "We don't just protect your content; we actively recover your lost conversions.",
      "Distribution intelligence merges with SERVED's legal operations: prioritized targets receive the same rigorous notices and follow-ups that keep removals durable across the stack.",
    ],
    standardQuote:
      '"Winning enforcement means collapsing networks, not chasing single links. We target the infrastructure that keeps piracy alive." - Founders',
  },

  "creator-protection-circle": {
    heroTitle: "Creator Protection Circle",
    heroSubtitle:
      "A private network built through years of working alongside some of the industry’s most recognized creators and brands.",
    mechanismHeading: "How It Works",
    mechanismBody:
      "Since 2020, our team has worked closely with a wide range of creators, including several top 0.1% earners - helping protect their content while supporting the growth of their digital brands. Over time, that work has developed into a trusted network spanning creators, agencies, platforms, and industry partners.\n\nRather than operating like a generic takedown service, SERVED functions as an extension of the creator ecosystem itself. When protection work is required, cases are handled by specialists familiar with the creator economy, the platforms creators rely on, and the networks where leaks typically circulate.\n\nThis same network also creates access beyond protection - connecting creators with other established talent, trusted partners, and brands across the space.",
    riveSrc: "/rive/network.riv",
    impactCards: [
      {
        title: "Dedicated Experience",
        body: "Critical leaks get human eyes and immediate action instead of waiting behind bulk automation.",
      },
      {
        title: "Industry Relationships",
        body: "Years of collaboration with creators, agencies, and brands have built a trusted network that allows protection issues to move quickly through the right channels when action is needed.",
      },
      {
        title: "Creator Network Access",
        body: "Working within this ecosystem naturally connects creators to a wider network of established talent, creating opportunities for collaboration, introductions, and shared visibility within the space.",
      },
    ],
    standardHeading: "The SERVED Approach: Enforced Rights",
    standardParagraphs: [
      "We don't just protect your content; we actively recover your lost conversions.",
      "Creator Protection Circle sits on top of the full SERVED stack - fingerprinting, visual AI, and deep monitoring - so escalation is informed by complete data, not isolated tickets.",
    ],
    standardQuote:
      '"When the stakes are high, speed and coordination matter most. We built this circle so critical incidents never wait." - Founders',
  },

  "secure-creator-infrastructure": {
    heroTitle: "Secure Creator Infrastructure",
    heroSubtitle:
      "Secure systems designed to protect your content, submissions, and communications.",
    mechanismHeading: "How It Works",
    mechanismBody:
      "Protecting creator content means handling sensitive media, personal information, and evidence files that should never circulate outside controlled systems. Our infrastructure is designed so everything shared with SERVED remains protected from the moment it enters our network.\n\nUploads, case evidence, and internal communications move through secure environments built specifically for rights protection work. Instead of relying on scattered email attachments or public file-sharing links, everything is handled through controlled systems that keep your content private while enforcement actions are carried out.",
    riveSrc: "/rive/encryption.riv",
    impactCards: [
      {
        title: "Encrypted File Transfers",
        body: "Sensitive content and evidence are submitted through encrypted channels rather than exposed email attachments or public cloud links.",
      },
      {
        title: "Evidence-Ready Documentation",
        body: "When removals, disputes, or impersonation cases require proof, supporting evidence is organized and preserved in formats platforms and legal teams recognize.",
      },
      {
        title: "Discreet Operations",
        body: "Protection work happens quietly in the background without exposing your vault, personal files, or private materials to unnecessary third parties.",
      },
    ],
    standardHeading: "The SERVED Approach: Enforced Rights",
    standardParagraphs: [
      "We don't just protect your content; we actively recover your lost conversions.",
      "Security architecture integrates with operational tooling: encrypted intake feeds the same case systems that drive takedowns, so protection of your data and protection of your rights stay one program.",
    ],
    standardQuote:
      '"Creator protection includes data protection. SERVED secures both because trust is part of enforcement." - Founders',
  },
};

/** @type {{ slug: string; label: string }[]} */
export const SERVICES_NAV = SERVICE_SLUGS.map((slug) => ({
  slug,
  label: SERVICES_BY_SLUG[slug].heroTitle,
}));

/**
 * @param {string | undefined} slug
 * @returns {ServiceContent | null}
 */
export function getServiceContent(slug) {
  if (!slug || !SERVICES_BY_SLUG[slug]) return null;
  return SERVICES_BY_SLUG[slug];
}
